import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * "Buy more, save more" tiers and related combos for the product page.
 *
 * Picking a tier sets the product form's quantity. Changing the quantity by hand
 * highlights the matching tier. Picking a combo switches Add to cart / Buy it now
 * to the combo product. Optionally swaps the main price to match the selection.
 *
 * Display only: tier discounts themselves come from Shopify automatic discounts.
 */
class QuantityBreaks extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;

  /** Quantity picked in this block, used when the theme's quantity box is hidden */
  #quantity = 1;

  /** @type {HTMLElement | null} Selected combo card, or null when buying this product */
  #combo = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('click', this.#onClick, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate, { signal });

    // The product form can be re-rendered (e.g. after adding to cart), so make sure
    // our product and quantity are in it right before Add to cart / Buy it now reads it.
    this.#root.addEventListener('click', this.#applyToForm, { signal, capture: true });
    this.#root.addEventListener('submit', this.#applyToForm, { signal, capture: true });

    const input = this.#quantitySelector()?.querySelector('input[name="quantity"]');
    this.#quantity = parseInt(input?.value ?? '1') || 1;
    this.#render();
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  get #root() {
    return this.closest('.shopify-section') ?? document;
  }

  /** @returns {any} */
  #quantitySelector() {
    return this.#root.querySelector('product-form-component quantity-selector-component');
  }

  /** @returns {HTMLElement[]} */
  get #options() {
    return Array.from(this.querySelectorAll('.quantity-breaks__option'));
  }

  /** @returns {HTMLElement[]} */
  get #tiers() {
    return Array.from(this.querySelectorAll('.quantity-breaks__option:not(.quantity-breaks__option--combo)'));
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const option = /** @type {HTMLElement | null} */ (
      event.target instanceof Element ? event.target.closest('.quantity-breaks__option') : null
    );
    if (!option) return;

    const isCombo = option.classList.contains('quantity-breaks__option--combo');
    this.#combo = isCombo ? option : null;
    this.#setQuantity(isCombo ? 1 : Number(option.dataset.quantity) || 1);
  };

  /**
   * Sets the theme's quantity box (or our own hidden field) and broadcasts the change
   * so the sticky bar and this block stay in sync.
   * @param {number} quantity
   */
  #setQuantity(quantity) {
    const selector = this.#quantitySelector();

    if (selector?.setValue) {
      selector.setValue(String(quantity));
      selector.onQuantityChange?.();
      selector.updateButtonStates?.();
    } else {
      this.dispatchEvent(new QuantitySelectorUpdateEvent(quantity));
    }
  }

  /** @param {Event & {detail?: {quantity: number, cartLine?: number}}} event */
  #onQuantityUpdate = (event) => {
    if (!event.detail || event.detail.cartLine) return;
    if (!(event.target instanceof Node) || !this.#root.contains(event.target)) return;

    this.#quantity = event.detail.quantity;
    this.#render();
  };

  /** Highlights the selected card, updates the main price and the product form. */
  #render() {
    let selected = this.#combo;

    if (!selected) {
      // Largest tier the quantity qualifies for
      selected = this.#tiers[0] ?? null;
      for (const tier of this.#tiers) {
        const tierQty = Number(tier.dataset.quantity) || 1;
        if (tierQty <= this.#quantity && tierQty >= (Number(selected?.dataset.quantity) || 1)) selected = tier;
      }
    }

    for (const option of this.#options) {
      const isSelected = option === selected;
      option.classList.toggle('is-selected', isSelected);
      option.setAttribute('aria-checked', String(isSelected));
    }

    this.#applyToForm();

    if (this.dataset.updateMainPrice !== 'true') return;

    if (this.#combo) {
      this.#setMainPrice(Number(this.#combo.dataset.comboPrice) || 0, Number(this.#combo.dataset.comboCompare) || 0);
      return;
    }

    const discount = Number(selected?.dataset.discount) || 0;
    const price = Number(this.dataset.price) || 0;
    if (discount > 0) {
      this.#setMainPrice(Math.round((price * (100 - discount)) / 100), price);
    } else {
      this.#setMainPrice(null);
    }
  }

  /**
   * Puts the selected product and quantity into the product form.
   * - Combo selected: the form's variant id becomes the combo's.
   * - Quantity box hidden: the form has no quantity field and would add 1, so add a hidden one.
   */
  #applyToForm = () => {
    const productForm = /** @type {HTMLElement | null} */ (this.#root.querySelector('product-form-component'));
    const form = productForm?.querySelector('form[data-type="add-to-cart-form"]');
    if (!productForm || !form) return;

    const idInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="id"]'));
    if (idInput) {
      if (this.#combo) {
        if (idInput.dataset.qbOriginalId === undefined) idInput.dataset.qbOriginalId = idInput.value;
        idInput.value = this.#combo.dataset.comboVariantId ?? idInput.value;
      } else if (idInput.dataset.qbOriginalId !== undefined) {
        idInput.value = idInput.dataset.qbOriginalId;
        delete idInput.dataset.qbOriginalId;
      }
    }

    if (this.#quantitySelector()) return;

    let input = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="quantity"]'));
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'quantity';
      form.append(input);
    }
    input.value = String(this.#quantity);
    productForm.dataset.quantityDefault = String(this.#quantity);
  };

  /**
   * Replaces the main and sticky-bar price. Pass null to restore the original.
   * @param {number | null} price
   * @param {number} [compareAt]
   */
  #setMainPrice(price, compareAt = 0) {
    const containers = this.#root.querySelectorAll(
      `product-price[data-product-id="${this.dataset.productId}"] [ref="priceContainer"], .sticky-add-to-cart__price [ref="priceContainer"]`
    );

    for (const container of containers) {
      if (!(container instanceof HTMLElement)) continue;

      if (price === null) {
        if (container.dataset.qbOriginal !== undefined) {
          container.innerHTML = container.dataset.qbOriginal;
          delete container.dataset.qbOriginal;
        }
        continue;
      }

      if (container.dataset.qbOriginal === undefined) container.dataset.qbOriginal = container.innerHTML;

      container.innerHTML =
        `<span class="price-item__group price"><bdi>${this.#money(price)}</bdi></span>` +
        (compareAt > price
          ? `<span class="price-item__group price-item--regular compare-at-price"><bdi>${this.#money(compareAt)}</bdi></span>`
          : '');
    }
  }

  /** @param {number} cents */
  #money(cents) {
    return formatMoney(cents, this.dataset.moneyFormat || '{{amount}}', this.dataset.currency || 'PKR');
  }
}

if (!customElements.get('quantity-breaks')) {
  customElements.define('quantity-breaks', QuantityBreaks);
}
